import {
  ComputerDesktopIcon,
  MoonIcon,
  SunIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownOption, DropdownSelector } from "./DropdownSelector";

export enum Themes {
  light = "light",
  dark = "dark",
  system = "system",
}

export const themeChangeEvent = new Event("themeChange");

const getSystemTheme = (): Themes =>
  window.matchMedia("(prefers-color-scheme: dark)").matches
    ? Themes.dark
    : Themes.light;

const getStoredTheme = (): Themes => {
  const localTheme = localStorage.getItem("theme");
  return Object.values(Themes).includes(localTheme as Themes)
    ? (localTheme as Themes)
    : Themes.system;
};

export const getTheme = (): Themes => {
  const theme = getStoredTheme();
  if (theme === Themes.system) {
    return getSystemTheme();
  } else {
    return theme;
  }
};

export default function ThemeSelector() {
  const { t } = useTranslation("ThemeSelector");
  const [selectedMode, setSelectedMode] = useState<Themes>(getStoredTheme());
  const [theme, setTheme] = useState<Themes>(
    selectedMode === Themes.system ? getSystemTheme() : selectedMode,
  );

  const applyTheme = useCallback((newTheme: Themes) => {
    document.querySelector("html")?.setAttribute("data-theme", newTheme);
    window.dispatchEvent(themeChangeEvent);
  }, []);

  const handleThemeModeChange = (mode: Themes) => {
    setSelectedMode(mode);
    localStorage.setItem("theme", mode);
    const newTheme = mode === Themes.system ? getSystemTheme() : mode;
    setTheme(newTheme);
    applyTheme(newTheme);
  };

  useEffect(() => {
    const systemThemeMediaQuery = window.matchMedia(
      "(prefers-color-scheme: dark)",
    );

    const handleSystemChange = (e: MediaQueryListEvent) => {
      if (selectedMode === Themes.system) {
        const newTheme = e.matches ? Themes.dark : Themes.light;
        setTheme(newTheme);
        applyTheme(newTheme);
      }
    };

    if (selectedMode === Themes.system) {
      systemThemeMediaQuery.addEventListener("change", handleSystemChange);
    }

    return () => {
      systemThemeMediaQuery.removeEventListener("change", handleSystemChange);
    };
  }, [selectedMode, applyTheme]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  const getThemeIcon = (themeMode: Themes) => {
    switch (themeMode) {
      case Themes.light:
        return <SunIcon className="h-5 w-5" />;
      case Themes.dark:
        return <MoonIcon className="h-5 w-5" />;
      case Themes.system:
        return <ComputerDesktopIcon className="h-5 w-5" />;
      default:
        return <SunIcon className="h-5 w-5" />;
    }
  };

  const options: DropdownOption[] = [
    Themes.light,
    Themes.dark,
    Themes.system,
  ].map((mode) => ({
    key: mode,
    label: t(mode),
    icon: getThemeIcon(mode),
    onClick: () => handleThemeModeChange(mode),
    isActive: selectedMode === mode,
  }));

  return (
    <DropdownSelector
      triggerIcon={
        theme === Themes.light ? (
          <SunIcon className="h-5 w-5" />
        ) : (
          <MoonIcon className="h-5 w-5" />
        )
      }
      options={options}
    />
  );
}
